import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Review } from '@limo/database'
import { getReviewSourceLabel } from '@limo/shared'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { StarRating } from '@/components/ui/star-rating'
import { ReviewDialog } from '@/components/reviews/ReviewDialog'
import { useToast } from '@/hooks/use-toast'
import { Plus, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'

export function ReviewsPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [editReviewId, setEditReviewId] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Use impersonated account when admin is impersonating, otherwise use real account
  const activeAccount = isImpersonating ? impersonatedAccount : account

  const { data: reviews, isLoading } = useQuery({
    queryKey: ['reviews', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as Review[]
    },
    enabled: !!activeAccount?.id,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('reviews').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      toast({
        title: 'Review deleted',
        description: 'The review has been successfully deleted.',
      })
      setDeleteId(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reviews</h1>
          <p className="text-muted-foreground">
            Manage customer reviews for your account
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Review
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle>All Reviews</CardTitle>
          <CardDescription>
            Customer testimonials displayed on your website.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Rating</TableHead>
                <TableHead>Reviewer</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="max-w-[300px]">Review</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-8" />
                    </TableCell>
                  </TableRow>
                ))
              ) : reviews?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    No reviews found. Add your first review to get started.
                  </TableCell>
                </TableRow>
              ) : (
                reviews?.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell
                      className="cursor-pointer"
                      onClick={() => setEditReviewId(review.id)}
                    >
                      <StarRating rating={review.rating} size="sm" />
                    </TableCell>
                    <TableCell
                      className="cursor-pointer font-medium hover:underline"
                      onClick={() => setEditReviewId(review.id)}
                    >
                      {review.reviewer_name}
                    </TableCell>
                    <TableCell
                      className="cursor-pointer"
                      onClick={() => setEditReviewId(review.id)}
                    >
                      <Badge variant="secondary">
                        {getReviewSourceLabel(review.source)}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className="cursor-pointer"
                      onClick={() => setEditReviewId(review.id)}
                    >
                      {review.title}
                    </TableCell>
                    <TableCell
                      className="cursor-pointer text-muted-foreground max-w-[300px]"
                      onClick={() => setEditReviewId(review.id)}
                    >
                      {truncateText(review.content, 60)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(review.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setEditReviewId(review.id)}
                            className="cursor-pointer"
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteId(review.id)}
                            className="text-destructive cursor-pointer"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the review. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {activeAccount && (
        <>
          <ReviewDialog
            reviewId={null}
            accountId={activeAccount.id}
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
          />

          <ReviewDialog
            reviewId={editReviewId}
            accountId={activeAccount.id}
            open={!!editReviewId}
            onOpenChange={(open) => !open && setEditReviewId(null)}
          />
        </>
      )}
    </div>
  )
}
